import {
  CopyMode,
  MoldOrderFirstLevelType,
  MoldOrderType,
} from '../../types/mold';
import { BaseMoldOrderModel } from './base';
import { MaintenanceMoldOrderModel } from './maintenance';
import { RepairMoldOrderModel } from './repair';
import { MoldAcceptanceMoldOrderModel } from './moldAcceptance';
import { MoldExportMoldOrderModel } from './moldExport';

/**
 * Factory for creating mold order models based on type
 * Provides a centralized way to get the appropriate model instance
 */
export class MoldOrderModelFactory {
  private static models: Record<MoldOrderFirstLevelType, BaseMoldOrderModel> = {
    maintenance: new MaintenanceMoldOrderModel(),
    repair: new RepairMoldOrderModel(),
    modify: new RepairMoldOrderModel(),
    mold_acceptance: new MoldAcceptanceMoldOrderModel(),
    mold_export: new MoldExportMoldOrderModel(),
  };

  /**
   * Get the appropriate model for a given mold order type
   */
  static getModel(
    type: MoldOrderFirstLevelType,
    subType?: MoldOrderType,
  ): BaseMoldOrderModel {
    const model = this.models[type];
    if (!model) {
      throw new Error(`Unknown mold order type: ${type}`);
    }
    return model;
  }

  /**
   * Get all available models
   */
  static getAllModels(): Record<MoldOrderFirstLevelType, BaseMoldOrderModel> {
    return this.models;
  }

  /**
   * Get model types with their labels
   */
  static getModelTypes(): Array<{
    type: MoldOrderFirstLevelType;
    label: string;
    description: string;
  }> {
    return Object.entries(this.models).map(([type, model]) => ({
      type: type as MoldOrderFirstLevelType,
      label: model.label,
      description: model.description,
    }));
  }

  /**
   * Check if a model type exists
   */
  static hasModel(type: MoldOrderFirstLevelType): boolean {
    return type in this.models;
  }

  /**
   * Get default model (maintenance)
   */
  static getDefaultModel(): BaseMoldOrderModel {
    return this.models.maintenance;
  }
}

/**
 * Convenience functions for common operations
 */

/**
 * Get model for the given type with null safety
 */
export function getMoldOrderModel(
  type: MoldOrderFirstLevelType | null,
  subType: MoldOrderType | null,
): BaseMoldOrderModel {
  if (!type) {
    return MoldOrderModelFactory.getDefaultModel();
  }
  // Convert null to undefined for subType to match getModel signature
  return MoldOrderModelFactory.getModel(
    type,
    subType === null ? undefined : subType,
  );
}

/**
 * Get header style configuration for a mold order type
 */
export function getHeaderStyleConfig(
  type: MoldOrderFirstLevelType | null,
  subType?: MoldOrderType | null,
) {
  const _model = getMoldOrderModel(
    type,
    subType === undefined ? null : subType,
  );
  return {
    headerBgColor: _model.headerBgColor,
    headerBorderColor: _model.headerBorderColor,
    headerShadow: _model.headerShadow,
    headerTextColor: _model.headerTextColor,
  };
}

/**
 * Get icon color for a mold order type
 */
export function getIconColorForType(
  type: MoldOrderFirstLevelType | null,
  subType?: MoldOrderType | null,
): string {
  const _model = getMoldOrderModel(
    type,
    subType === undefined ? null : subType,
  );
  return _model.iconColor;
}

/**
 * Get label for a mold order type
 */
export function getMoldOrderMainTypeLabel(
  type: MoldOrderFirstLevelType | null,
  subType?: MoldOrderType | null,
): string {
  if (!type) return '';
  const _model = getMoldOrderModel(
    type,
    subType === undefined ? null : subType,
  );
  return _model.label;
}

/**
 * Validate order detail using the appropriate model
 */
export function validateOrderDetail(
  type: MoldOrderFirstLevelType | null,
  orderDetail: any,
  subType?: MoldOrderType | null,
) {
  const _model = getMoldOrderModel(
    type,
    subType === undefined ? null : subType,
  );
  return _model.validateOrderDetail(orderDetail);
}

/**
 * Create initial order detail using the appropriate model
 */
export function createInitialOrderDetail(
  type: MoldOrderFirstLevelType | null,
  subType: MoldOrderType | string | null = null,
  params: Record<string, any> = {},
) {
  // Ensure subType is MoldOrderType or null
  const normalizedSubType =
    subType === undefined || subType === null
      ? null
      : (subType as MoldOrderType);
  const model = getMoldOrderModel(type, normalizedSubType);
  const isSubOrder = params.isSubOrder || false;
  if (normalizedSubType) {
    return model.createInitialOrderDetailForType(normalizedSubType, isSubOrder);
  }
  return model.createInitialOrderDetail();
}

/**
 * Transform order detail from previous order using the appropriate model
 */
export function transformFromPreviousOrder(
  type: MoldOrderFirstLevelType | null,
  previousOrder: any,
  copyMode: CopyMode = 'none',
  carriedForm: Record<string, any> = {},
  targetType?: MoldOrderType,
  carryKeys: string[] = [],
) {
  const _model = getMoldOrderModel(type, null);
  return _model.transformFromPreviousOrder(
    previousOrder,
    copyMode,
    carriedForm,
    targetType,
    carryKeys,
  );
}
